import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTable, usePagination, useGlobalFilter, useAsyncDebounce, useSortBy } from "react-table";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import SoftBox from "components/SoftBox";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import SoftTypography from "components/SoftTypography";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";

function QuestDataTable({
  entriesPerPage = { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch = false,
  showTotalEntries = true,
  table,
  pagination = { variant: "gradient", color: "info" },
  isSorted = true,
  noEndBorder = false,
  emptyDataMessage = "No records found.",
}) {
  const columns = useMemo(() => table.columns, [table.columns]);
  const data = useMemo(() => table.rows, [table.rows]);
  const hasInternalPagination = entriesPerPage !== false;
  const defaultValue = hasInternalPagination
    ? entriesPerPage?.defaultValue || 10
    : Math.max(data.length, 1);

  const tableInstance = useTable(
    {
      columns,
      data,
      autoResetPage: false,
      initialState: { pageIndex: 0, pageSize: defaultValue },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
  } = tableInstance;

  useEffect(() => {
    setPageSize(defaultValue);
  }, [defaultValue, setPageSize]);

  useEffect(() => {
    if (pageOptions.length > 0 && pageIndex > pageOptions.length - 1) {
      gotoPage(pageOptions.length - 1);
    }
  }, [gotoPage, pageIndex, pageOptions.length]);

  const [search, setSearch] = useState(globalFilter || "");
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 100);

  const setSortedValue = (column) => {
    if (isSorted && column.isSorted) return column.isSortedDesc ? "desc" : "asce";
    if (isSorted) return "none";
    return false;
  };

  const renderPagination = pageOptions.map((option) => (
    <SoftPagination item key={option} onClick={() => gotoPage(option)} active={pageIndex === option}>
      {option + 1}
    </SoftPagination>
  ));

  const customizedPageOptions = pageOptions.map((option) => option + 1);
  const handleInputPaginationValue = ({ target: { value } }) => {
    const nextPageIndex = Number(value) - 1;
    gotoPage(nextPageIndex >= 0 && nextPageIndex < pageOptions.length ? nextPageIndex : 0);
  };

  const entriesStart = rows.length === 0 ? 0 : pageIndex * pageSize + 1;
  const entriesEnd = Math.min(rows.length, pageSize * (pageIndex + 1));
  const showPagination = hasInternalPagination && pageOptions.length > 1;

  return (
    <TableContainer sx={{ boxShadow: "none" }}>
      {(hasInternalPagination || canSearch) && (
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={1}>
          {showTotalEntries && (
            <SoftBox color="text" fontSize="0.875rem">
              Showing {entriesStart} to {entriesEnd} of {rows.length} entries
            </SoftBox>
          )}
          {canSearch && (
            <SoftBox width="12rem" ml="auto">
              <SoftInput
                placeholder="Search..."
                value={search}
                onChange={({ currentTarget }) => {
                  setSearch(currentTarget.value);
                  onSearchChange(currentTarget.value);
                }}
              />
            </SoftBox>
          )}
        </SoftBox>
      )}

      <Table {...getTableProps()}>
        <SoftBox component="thead">
          {headerGroups.map((headerGroup, headerGroupKey) => (
            <TableRow key={headerGroupKey} {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column, columnKey) => (
                <DataTableHeadCell
                  key={columnKey}
                  {...column.getHeaderProps(isSorted && column.getSortByToggleProps())}
                  width={column.width || "auto"}
                  align={column.align || "left"}
                  sorted={setSortedValue(column)}
                >
                  {column.render("Header")}
                </DataTableHeadCell>
              ))}
            </TableRow>
          ))}
        </SoftBox>
        <TableBody {...getTableBodyProps()}>
          {page.length === 0 ? (
            <TableRow>
              <SoftBox component="td" colSpan={columns.length || 1} textAlign="center" py={3}>
                <SoftTypography variant="body2" color="text">
                  {emptyDataMessage}
                </SoftTypography>
              </SoftBox>
            </TableRow>
          ) : (
            page.map((row, rowKey) => {
              prepareRow(row);
              return (
                <TableRow key={rowKey} {...row.getRowProps()}>
                  {row.cells.map((cell, cellKey) => (
                    <DataTableBodyCell
                      key={cellKey}
                      noBorder={noEndBorder && page.length - 1 === rowKey}
                      align={cell.column.align || "left"}
                      {...cell.getCellProps()}
                    >
                      {cell.render("Cell")}
                    </DataTableBodyCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      {showPagination && (
        <SoftBox
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="flex-end"
          alignItems={{ xs: "flex-start", sm: "center" }}
          p={3}
        >
          <SoftPagination
            variant={pagination.variant || "gradient"}
            color={pagination.color || "info"}
          >
            {canPreviousPage && (
              <SoftPagination item onClick={() => previousPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_left</Icon>
              </SoftPagination>
            )}
            {renderPagination.length > 6 ? (
              <SoftBox width="5rem" mx={1}>
                <SoftInput
                  inputProps={{ type: "number", min: 1, max: customizedPageOptions.length }}
                  value={customizedPageOptions[pageIndex] || 1}
                  onChange={handleInputPaginationValue}
                />
              </SoftBox>
            ) : (
              renderPagination
            )}
            {canNextPage && (
              <SoftPagination item onClick={() => nextPage()}>
                <Icon sx={{ fontWeight: "bold" }}>chevron_right</Icon>
              </SoftPagination>
            )}
          </SoftPagination>
        </SoftBox>
      )}
    </TableContainer>
  );
}

QuestDataTable.propTypes = {
  entriesPerPage: PropTypes.oneOfType([
    PropTypes.shape({
      defaultValue: PropTypes.number,
      entries: PropTypes.arrayOf(PropTypes.number),
    }),
    PropTypes.bool,
  ]),
  canSearch: PropTypes.bool,
  showTotalEntries: PropTypes.bool,
  table: PropTypes.shape({
    columns: PropTypes.array.isRequired,
    rows: PropTypes.array.isRequired,
  }).isRequired,
  pagination: PropTypes.shape({
    variant: PropTypes.oneOf(["contained", "gradient"]),
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "light",
    ]),
  }),
  isSorted: PropTypes.bool,
  noEndBorder: PropTypes.bool,
  emptyDataMessage: PropTypes.node,
};

export default QuestDataTable;
